import { useMemo, useState } from 'react'
import useNotificationStore from '../../services/notificationStore'
import Button from '../ui/Button'
import ConfirmDialog from '../ui/ConfirmDialog'
import { ui } from '../../styles/ui'

export default function NotificationInbox({ onNavigate }) {
  const notifications = useNotificationStore((state) => state.notifications)
  const markRead = useNotificationStore((state) => state.markRead)
  const markAllRead = useNotificationStore((state) => state.markAllRead)
  const archive = useNotificationStore((state) => state.archive)
  const deleteNotification = useNotificationStore((state) => state.deleteNotification)
  const showToast = useNotificationStore((state) => state.showToast)
  const [status, setStatus] = useState('all')
  const [severity, setSeverity] = useState('all')
  const [channel, setChannel] = useState('all')
  const [pendingDelete, setPendingDelete] = useState(null)

  const filtered = useMemo(() => {
    return (notifications || []).filter((item) => {
      if (status !== 'all' && item.status !== status) return false
      if (severity !== 'all' && item.severity !== severity) return false
      if (channel !== 'all' && item.channel !== channel) return false
      return true
    })
  }, [notifications, status, severity, channel])

  return (
    <div className={`${ui.cards.base} w-[min(92vw,44rem)] overflow-hidden p-0 shadow-lg`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-divider bg-surface-elevated px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
        <Button size="sm" variant="ghost" onClick={() => markAllRead()}>
          Mark all read
        </Button>
      </div>

      <div className="grid gap-2 border-b border-divider bg-surface px-4 py-3 sm:grid-cols-3">
        <select aria-label="Filter status" className={ui.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All status</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
          <option value="archived">Archived</option>
        </select>
        <select aria-label="Filter severity" className={ui.select} value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="all">All severity</option>
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
        </select>
        <select aria-label="Filter channel" className={ui.select} value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="all">All channels</option>
          <option value="operations">Operations</option>
          <option value="queue">Queue</option>
          <option value="recognition">Recognition</option>
          <option value="i18n">i18n</option>
          <option value="system">System</option>
          <option value="legal">Legal</option>
          <option value="app_update">App update</option>
        </select>
      </div>

      <div className="max-h-96 space-y-2 overflow-auto p-4">
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-outline p-4 text-sm text-muted-foreground">No notifications</div>
        ) : filtered.map((item) => (
          <div key={item.id || item.dedupeKey} className="rounded-lg border border-outline bg-surface p-3 shadow-sm transition-colors hover:bg-surface-elevated">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{item.channel || 'system'} · {item.severity || 'info'}</p>
                <p className="text-sm font-semibold">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.message}</p>
              </div>
              <div className="flex gap-1">
                {item.status === 'unread' && (
                  <Button size="sm" variant="ghost" onClick={() => markRead(item.id)}>Read</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => archive(item.id)}>Archive</Button>
                <Button size="sm" variant="danger" onClick={() => setPendingDelete(item)}>Delete</Button>
              </div>
            </div>
            {item.actionUrl && (
              <button
                type="button"
                onClick={() => onNavigate?.(item.actionUrl)}
                className="mt-2 text-xs font-semibold text-primary hover:underline"
              >
                Open action
              </button>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete notification"
        message={pendingDelete ? `Delete notification \"${pendingDelete.title}\" from inbox?` : 'Delete this notification from inbox?'}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={async () => {
          if (!pendingDelete?.id) return
          try {
            if (typeof deleteNotification === 'function') {
              await deleteNotification(pendingDelete.id)
            } else if (typeof archive === 'function') {
              // WHY: tolerate stale store snapshots/chunks where delete action is missing.
              await archive(pendingDelete.id)
            } else {
              throw new Error('Notification delete action is unavailable')
            }
            setPendingDelete(null)
          } catch (error) {
            showToast({
              severity: 'error',
              title: 'Delete failed',
              message: error?.message || 'Unable to delete notification',
            })
          }
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}
